import type { ComponentType } from 'react'
import { lazy, type LazyExoticComponent } from 'react'
import { getCurrentScreen } from '@auth0/auth0-acul-js'

export type ScreenName =
  | 'login'
  | 'login-id'
  | 'login-password'
  | 'reset-password-request'
  | 'reset-password'
  | 'reset-password-success'
  | 'reset-password-error'
  | 'signup'
  | 'signup-id'
  | 'signup-password'
  | 'phone-identifier-challenge'
  | 'passkey-enrollment'
  | 'passkey-enrollment-local'
  | 'mfa-email-challenge'
  | 'mfa-sms-challenge'
  | 'mfa-sms-challenge-activation'
  | 'mfa-email-enrollment'
  | 'mfa-sms-enrollment'
  | 'organization-picker'
  | 'components-preview'

type ScreenModule = { default: ComponentType }

const SCREEN_COMPONENTS: Record<
  ScreenName,
  LazyExoticComponent<ComponentType>
> = {
  login: lazy(() => import('@/screens/login') as Promise<ScreenModule>),
  'login-id': lazy(() => import('@/screens/login-id') as Promise<ScreenModule>),
  'login-password': lazy(
    () => import('@/screens/login-password') as Promise<ScreenModule>,
  ),
  'reset-password-request': lazy(
    () => import('@/screens/reset-password-request') as Promise<ScreenModule>,
  ),
  'reset-password': lazy(
    () => import('@/screens/reset-password') as Promise<ScreenModule>,
  ),
  'reset-password-success': lazy(
    () => import('@/screens/reset-password-success') as Promise<ScreenModule>,
  ),
  'reset-password-error': lazy(
    () => import('@/screens/reset-password-error') as Promise<ScreenModule>,
  ),
  signup: lazy(() => import('@/screens/signup') as Promise<ScreenModule>),
  'signup-id': lazy(() => import('@/screens/signup-id') as Promise<ScreenModule>),
  'phone-identifier-challenge': lazy(
    () => import('@/screens/phone-identifier-challenge') as Promise<ScreenModule>,
  ),
  'signup-password': lazy(
    () => import('@/screens/signup-password') as Promise<ScreenModule>,
  ),
  'passkey-enrollment': lazy(
    () => import('@/screens/passkey-enrollment') as Promise<ScreenModule>,
  ),
  'passkey-enrollment-local': lazy(
    () =>
      import('@/screens/passkey-enrollment-local') as Promise<ScreenModule>,
  ),
  'mfa-email-challenge': lazy(
    () => import('@/screens/mfa-email-challenge') as Promise<ScreenModule>,
  ),
  'mfa-sms-challenge': lazy(
    () => import('@/screens/mfa-sms-challenge') as Promise<ScreenModule>,
  ),
  // Só preview: no tenant é a própria `mfa-sms-challenge`, em modo ativação
  // pelo marcador que a `mfa-sms-enrollment` grava.
  'mfa-sms-challenge-activation': lazy(
    () =>
      import('@/screens/mfa-sms-challenge-activation') as Promise<ScreenModule>,
  ),
  'mfa-email-enrollment': lazy(
    () => import('@/screens/mfa-email-enrollment') as Promise<ScreenModule>,
  ),
  'mfa-sms-enrollment': lazy(
    () => import('@/screens/mfa-sms-enrollment') as Promise<ScreenModule>,
  ),
  'organization-picker': lazy(
    () => import('@/screens/organization-picker') as Promise<ScreenModule>,
  ),
  'components-preview': lazy(
    () => import('@/screens/components-preview') as Promise<ScreenModule>,
  ),
}

/**
 * Título da aba por tela (guia B4 §5: o `<title>` descreve a tela). No
 * Universal Login o documento é do Auth0; a tela troca o título ao montar.
 */
const SCREEN_TITLES: Record<ScreenName, string> = {
  login: 'Entrar',
  'login-id': 'Entrar',
  'login-password': 'Entrar',
  'reset-password-request': 'Esqueci minha senha',
  'reset-password': 'Nova senha',
  'reset-password-success': 'Senha alterada',
  'reset-password-error': 'Link expirado',
  signup: 'Criar conta',
  'signup-id': 'Criar conta',
  'signup-password': 'Criar conta',
  'phone-identifier-challenge': 'Confirme seu celular',
  'passkey-enrollment': 'Chave de acesso',
  'passkey-enrollment-local': 'Chave de acesso',
  'mfa-email-challenge': 'Confirme seu e-mail',
  'mfa-sms-challenge': 'Confirme seu celular',
  'mfa-sms-challenge-activation': 'Ativar confirmação por SMS',
  'mfa-email-enrollment': 'Ativar confirmação por e-mail',
  'mfa-sms-enrollment': 'Ativar confirmação por SMS',
  'organization-picker': 'Sua organização',
  'components-preview': 'Componentes',
}

export function screenTitle(name: string): string {
  const title = SCREEN_TITLES[name as ScreenName]
  return title ? `${title} · Alelo` : 'Alelo'
}

export function resolveScreen(
  name: string,
): LazyExoticComponent<ComponentType> | null {
  if (name in SCREEN_COMPONENTS) {
    return SCREEN_COMPONENTS[name as ScreenName]
  }
  return null
}

/**
 * Detecta a screen do Universal Login no Custom Domain (acesso-b2c-dev.siteteste.inf.br).
 * Em runtime Auth0: `window.universal_login_context` via SDK.
 * Em preview local: `?screen=` ou data attributes.
 */
export function detectAuth0Screen(): string | null {
  try {
    const fromSdk = getCurrentScreen()
    if (fromSdk) return fromSdk
  } catch {
    // Fora do contexto UL (preview local) — segue fallbacks
  }

  const ctx = (
    window as Window & {
      universal_login_context?: { screen?: { name?: string } }
    }
  ).universal_login_context
  if (ctx?.screen?.name) return ctx.screen.name

  const dataScreen = document.documentElement.dataset.screen
  if (dataScreen) return dataScreen

  const meta = document.querySelector('meta[name="auth0-screen"]')
  if (meta) return meta.getAttribute('content')

  return null
}
